/**
 * =============================================================================
 * Super A Sports — shared front-end behavior (child.js)
 * =============================================================================
 * Implements exactly the 11 rows that docs/build/block-map.md §5.1 assigns to
 * child.js. Everything else is a block-level view.js (S3) and must not appear
 * here.
 *
 *  #  Trigger                              Owner in this file
 *  1  setInterval(rotateMs = 4200)         initAnnouncement / rotateAnnouncement
 *  2  <= 760px -> CSS saTicker marquee     initAnnouncement / isTickerMode
 *  3  hamburger click                      openDrawer
 *  4  drawer close (fa-xmark)              closeDrawer
 *  5  drawer backdrop click                initDrawer -> closeDrawer
 *  6  Escape while drawer open             onDocumentKeydown -> closeDrawer
 *  7  drawer row caret (exclusive)         initDrawerAccordion / toggleDrawerSection
 *  8  top nav mouseenter                   initMegaMenu / openMega
 *  9  <header> mouseleave                  initMegaMenu / closeMega
 * 10  document 'sa:toast' CustomEvent      initToast / showToast
 * 11  WhatsApp FAB + window.saWa(text)     initWhatsApp / openWhatsApp
 *                                          number resolved by waNumber()
 *
 * Plus imported modules for markup that Woo, not a block, renders (so it is
 * not in block-map section 5.1):
 *  C  Woo notice close button + auto-close  ./wc-notices.js / initWcNotices
 *  G  product gallery "1 / 3" counter       ./product-gallery.js / initProductGallery
 *
 * Vanilla JS, no jQuery, no framework. Every selector below exists in
 * parts/header.html / parts/footer.html.
 */

import { initWcNotices } from "./wc-notices.js";
import { initProductGallery } from "./product-gallery.js";

(function () {
	"use strict";

	// -------------------------------------------------------------------------
	// Constants taken from component.jsx (do not invent values)
	// -------------------------------------------------------------------------
	var ANNOUNCE_MS = 4200; // componentDidMount: Math.max(1500, announcementSpeed || 4200)
	var ANNOUNCE_MIN_MS = 1500;
	var ANNOUNCE_FADE_MS = 280; // matches .sa-announce__msg transition
	var TOAST_MS = 2800; // flash(): setTimeout(..., 2800)
	// Fallback only. The live number comes from the site-whatsapp-fab block
	// (block.json attribute `number`) — see waNumber() at row 11.
	var WA_NUMBER_FALLBACK = "6590702836"; // WA_NUMBER
	var WA_DEFAULT_MSG = "Hi Super A Sports! I have a question about";

	var FOCUSABLE =
		'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

	function qs(sel, root) {
		return (root || document).querySelector(sel);
	}

	function qsa(sel, root) {
		return Array.prototype.slice.call((root || document).querySelectorAll(sel));
	}

	// =========================================================================
	// Rows 1 + 2 — announcement bar: desktop crossfade rotator / phone marquee
	// =========================================================================
	var announce = {
		bar: null,
		msgEl: null,
		ticker: null,
		messages: [],
		index: 0,
		timer: null,
	};

	/**
	 * Row 2 — below 760px the bar becomes a pure CSS `saTicker` marquee over the
	 * doubled list (see _header.scss). The rotator timer is pointless then, so we
	 * detect the swap from the computed display of the ticker element and let CSS
	 * own the animation. `--sa-tick` (26s -> 22s) is a token, never touched here.
	 */
	function isTickerMode() {
		if (!announce.ticker) {
			return false;
		}
		return window.getComputedStyle(announce.ticker).display !== "none";
	}

	function rotateAnnouncement() {
		if (!announce.msgEl || announce.messages.length < 2) {
			return;
		}

		if (isTickerMode()) {
			return; // CSS marquee is on screen — nothing to rotate
		}

		announce.index = (announce.index + 1) % announce.messages.length;

		var next = announce.messages[announce.index];
		announce.msgEl.classList.add("is-out");

		window.setTimeout(function () {
			announce.msgEl.textContent = next;
			announce.msgEl.classList.remove("is-out");
		}, ANNOUNCE_FADE_MS);
	}

	function initAnnouncement() {
		announce.bar = qs("[data-sa-announce]");
		if (!announce.bar) {
			return;
		}

		announce.msgEl = qs("[data-sa-announce-msg]", announce.bar);
		announce.ticker = qs("[data-sa-ticker]", announce.bar);

		// The ticker track holds the doubled list — the first half is the source of
		// truth for the rotator, so the two modes can never drift apart.
		var items = qsa(".sa-announce__item", announce.bar).map(function (el) {
			return el.textContent.trim();
		});
		var unique = [];
		for (var i = 0; i < items.length; i++) {
			if (unique.indexOf(items[i]) === -1) {
				unique.push(items[i]);
			}
		}
		announce.messages = unique.length
			? unique
			: announce.msgEl
			? [announce.msgEl.textContent.trim()]
			: [];

		if (!announce.msgEl || announce.messages.length < 2) {
			return;
		}

		announce.msgEl.textContent = announce.messages[0];

		var attr = parseInt(announce.bar.getAttribute("data-sa-announce-ms"), 10);
		var ms = Math.max(ANNOUNCE_MIN_MS, isNaN(attr) ? ANNOUNCE_MS : attr);

		announce.timer = window.setInterval(rotateAnnouncement, ms);
	}

	// =========================================================================
	// Rows 3–7 — mobile drawer
	// =========================================================================
	var drawer = {
		root: null,
		panel: null,
		burger: null,
		closeBtn: null,
		lastFocus: null,
		prevOverflow: "",
		open: false,
	};

	function drawerFocusables() {
		if (!drawer.panel) {
			return [];
		}
		return qsa(FOCUSABLE, drawer.panel).filter(function (el) {
			return el.offsetWidth > 0 || el.offsetHeight > 0;
		});
	}

	/** Row 3 — hamburger opens the slide-in drawer and locks body scroll. */
	function openDrawer() {
		if (!drawer.root || drawer.open) {
			return;
		}

		drawer.lastFocus = document.activeElement;
		drawer.open = true;
		drawer.root.hidden = false;
		drawer.root.classList.add("is-open");

		if (drawer.burger) {
			drawer.burger.setAttribute("aria-expanded", "true");
		}

		// setMenu(true): document.body.style.overflow = "hidden"
		drawer.prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		if (drawer.closeBtn) {
			drawer.closeBtn.focus();
		}
	}

	/** Rows 4 + 5 + 6 — close, restore scroll, collapse every accordion section. */
	function closeDrawer() {
		if (!drawer.root || !drawer.open) {
			return;
		}

		drawer.open = false;
		drawer.root.classList.remove("is-open");
		drawer.root.hidden = true;

		if (drawer.burger) {
			drawer.burger.setAttribute("aria-expanded", "false");
		}

		// setMenu(false): restore overflow and reset mobSec
		document.body.style.overflow = drawer.prevOverflow || "";
		closeAllDrawerSections();

		if (drawer.lastFocus && typeof drawer.lastFocus.focus === "function") {
			drawer.lastFocus.focus();
		}
		drawer.lastFocus = null;
	}

	function trapDrawerFocus(event) {
		var items = drawerFocusables();
		if (!items.length) {
			return;
		}

		var first = items[0];
		var last = items[items.length - 1];

		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	}

	function initDrawer() {
		drawer.root = qs("[data-sa-drawer]");
		drawer.burger = qs("[data-sa-burger]");

		if (drawer.burger) {
			drawer.burger.addEventListener("click", function () {
				if (drawer.open) {
					closeDrawer();
				} else {
					openDrawer();
				}
			});
		}

		if (!drawer.root) {
			return;
		}

		drawer.panel = qs(".sa-drawer__panel", drawer.root);
		drawer.closeBtn = qs("[data-sa-drawer-close]", drawer.root);
		drawer.root.hidden = true;

		// Row 4 — the fa-xmark button
		if (drawer.closeBtn) {
			drawer.closeBtn.addEventListener("click", closeDrawer);
		}

		// Row 5 — backdrop click
		var backdrop = qs("[data-sa-drawer-backdrop]", drawer.root);
		if (backdrop) {
			backdrop.addEventListener("click", closeDrawer);
		}

		// Signed-out EPOS CRM widget: the plugin opens its login modal on click,
		// so step the drawer out of the way. Signed in, the widget is a list of
		// real links and is left alone.
		var epos = qs("[data-sa-drawer-epos] [data-epos-login-widget]", drawer.root);
		if (epos && !qs(".epos_crm_login_dropdown", epos)) {
			epos.addEventListener("click", closeDrawer);
		}

		// Navigating away closes it too (drawer links are real hrefs)
		qsa("a[href]", drawer.root).forEach(function (link) {
			link.addEventListener("click", function () {
				document.body.style.overflow = drawer.prevOverflow || "";
			});
		});

		drawer.root.addEventListener("keydown", function (event) {
			if (event.key === "Tab" && drawer.open) {
				trapDrawerFocus(event);
			}
		});
	}

	// ---- Row 7 — drawer accordion, only one section open at a time -----------
	function closeAllDrawerSections() {
		qsa("[data-sa-drawer-section]").forEach(function (section) {
			section.classList.remove("is-open");
			var toggle = qs("[data-sa-drawer-toggle]", section);
			if (toggle) {
				toggle.setAttribute("aria-expanded", "false");
			}
		});
	}

	function toggleDrawerSection(section) {
		var wasOpen = section.classList.contains("is-open");
		closeAllDrawerSections(); // mobSec is exclusive

		if (!wasOpen) {
			section.classList.add("is-open");
			var toggle = qs("[data-sa-drawer-toggle]", section);
			if (toggle) {
				toggle.setAttribute("aria-expanded", "true");
			}
		}
	}

	function initDrawerAccordion() {
		qsa("[data-sa-drawer-section]").forEach(function (section) {
			var toggle = qs("[data-sa-drawer-toggle]", section);
			if (!toggle) {
				return;
			}
			toggle.addEventListener("click", function () {
				toggleDrawerSection(section);
			});
			// The EPOS account row is the plugin's <div role="button">, not a
			// <button>, so it needs the keyboard activation a button gets free.
			if (toggle.tagName !== "BUTTON") {
				toggle.addEventListener("keydown", function (event) {
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault();
						toggleDrawerSection(section);
					}
				});
			}
		});
	}

	// =========================================================================
	// Rows 8 + 9 — desktop mega menu
	// =========================================================================
	function closeMega() {
		qsa(".sa-nav__item.is-open").forEach(function (item) {
			item.classList.remove("is-open");
		});
	}

	function openMega(item) {
		closeMega();
		if (item.querySelector(".sa-mega")) {
			item.classList.add("is-open");
		}
	}

	function initMegaMenu() {
		var header = qs("[data-sa-header]");
		if (!header) {
			return;
		}

		// Row 8 — item.enter
		qsa(".sa-nav__item", header).forEach(function (item) {
			item.addEventListener("mouseenter", function () {
				openMega(item);
			});
			// keyboard parity: CSS :focus-within shows the panel, this keeps the
			// class-driven state in sync so row 9 can always close it.
			item.addEventListener("focusin", function () {
				openMega(item);
			});
		});

		// Row 9 — closeMenu on header mouseleave
		header.addEventListener("mouseleave", closeMega);
		header.addEventListener("focusout", function (event) {
			if (!header.contains(event.relatedTarget)) {
				closeMega();
			}
		});
	}

	// =========================================================================
	// Row 10 — global toast, driven by the `sa:toast` CustomEvent
	// =========================================================================
	var toast = { root: null, msgEl: null, iconEl: null, ctaEl: null, timer: null };

	function showToast(message, tone) {
		if (!toast.root) {
			return;
		}

		if (toast.msgEl) {
			toast.msgEl.textContent = message || "";
		}

		// `tone: 'error'` — service forms must not show the cart success dressing.
		var isError = tone === "error";
		toast.root.classList.toggle("is-error", isError);
		if (toast.iconEl) {
			toast.iconEl.className = isError
				? "fa-solid fa-circle-exclamation"
				: toast.iconEl.saDefaultClass;
		}
		if (toast.ctaEl) {
			toast.ctaEl.hidden = isError;
		}

		toast.root.classList.add("is-visible");

		window.clearTimeout(toast.timer);
		toast.timer = window.setTimeout(function () {
			toast.root.classList.remove("is-visible");
			if (toast.msgEl) {
				toast.msgEl.textContent = "";
			}
		}, TOAST_MS);
	}

	function initToast() {
		toast.root = qs("[data-sa-toast]");
		toast.msgEl = qs("[data-sa-toast-msg]");
		toast.iconEl = toast.root ? toast.root.querySelector("i") : null;
		toast.ctaEl = toast.root
			? toast.root.querySelector(".sa-toast__cta")
			: null;
		if (toast.iconEl) {
			toast.iconEl.saDefaultClass = toast.iconEl.className;
		}

		// Contract for every block (block-map §5.9):
		//   document.dispatchEvent(new CustomEvent('sa:toast', { detail: '…' }))
		// detail may also be { message, tone: 'error' }.
		document.addEventListener("sa:toast", function (event) {
			var detail = event && event.detail;
			var message =
				typeof detail === "string"
					? detail
					: detail && detail.message
					? detail.message
					: "";
			showToast(message, detail && detail.tone);
		});

		window.saToast = showToast;
	}

	// =========================================================================
	// Row 11 — WhatsApp bubble + window.saWa(text) for every other block
	// =========================================================================
	/**
	 * The number is client-editable on `ai-zippy/site-whatsapp-fab`
	 * (block.json attribute `number`, default 6590702836), so it must never be
	 * read from a constant here — that made every editor change a no-op.
	 * Resolution order:
	 *
	 *   1. `data-sa-wa-number` — the agreed contract attribute. Nearest
	 *      ancestor first, so a single button can override the site number.
	 *   2. the FAB bubble's own href="https://wa.me/{number}?text=…", which
	 *      render.php already builds from the attribute (works today).
	 *   3. WA_NUMBER_FALLBACK, only when no FAB is on the page.
	 *
	 * Deliberately not cached: the FAB is re-rendered in the editor canvas and
	 * by WooCommerce cart fragments.
	 */
	function waDigits(value) {
		return String(value == null ? "" : value).replace(/\D+/g, "");
	}

	function waNumber(scope) {
		var carrier =
			(scope && scope.closest && scope.closest("[data-sa-wa-number]")) ||
			qs("[data-sa-wa-number]");

		if (carrier) {
			var explicit = waDigits(carrier.getAttribute("data-sa-wa-number"));
			if (explicit) {
				return explicit;
			}
		}

		var link = qs('.sa-fab__btn[href*="wa.me/"]') || qs('a[href*="wa.me/"]');
		if (link) {
			var match = /wa\.me\/(\d+)/.exec(link.getAttribute("href") || "");
			if (match) {
				return match[1];
			}
		}

		return WA_NUMBER_FALLBACK;
	}

	function openWhatsApp(message, scope) {
		var text = message || WA_DEFAULT_MSG;
		window.open(
			"https://wa.me/" + waNumber(scope) + "?text=" + encodeURIComponent(text),
			"_blank"
		);
	}

	function initWhatsApp() {
		qsa("[data-sa-wa]").forEach(function (button) {
			button.addEventListener("click", function (event) {
				event.preventDefault();
				openWhatsApp(button.getAttribute("data-sa-wa-message"), button);
			});
		});

		// Unchanged 1-argument contract: strings-catalog/view.js and the other
		// section scripts call window.saWa(text) and must keep working.
		window.saWa = openWhatsApp;
	}

	// =========================================================================
	// Row 6 — Escape closes the drawer (document-level so it also fires when the
	// focus escaped the panel)
	// =========================================================================
	function onDocumentKeydown(event) {
		if (event.key !== "Escape" && event.key !== "Esc") {
			return;
		}
		if (drawer.open) {
			closeDrawer();
			return;
		}
		closeMega();
	}

	// =========================================================================
	// Boot
	// =========================================================================
	function boot() {
		initAnnouncement(); // rows 1, 2
		initDrawer(); // rows 3, 4, 5
		initDrawerAccordion(); // row 7
		initMegaMenu(); // rows 8, 9
		initToast(); // row 10
		initWhatsApp(); // row 11
		initWcNotices(); // Woo notice banners: close button + auto-close
		initProductGallery(); // product page slider counter
		document.addEventListener("keydown", onDocumentKeydown); // row 6
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", boot);
	} else {
		boot();
	}
})();
